// Generic recent-item tracker factory used by the sidebar to render
// muscle-memory shortcuts for matters/clients/contacts.  All three follow the
// same shape: newest-first, capped at 10, entries older than 30 days pruned on
// read.  Each tracker dispatches a custom event on writes so the sidebar +
// other tabs (via the native 'storage' event) refresh immediately.  The lists
// are per browser, not per account: when someone else signs in,
// lib/quickListOwner.js empties them first.

const MAX = 10
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000

/**
 * Build a tracker for a specific item type.  Each tracker owns its own
 * localStorage key + custom-event name so they don't collide.
 *
 * @param {object} cfg
 * @param {string} cfg.storageKey       localStorage key (e.g. 'lextrack_recent_matters')
 * @param {string} cfg.eventName        custom event dispatched on writes (e.g. 'recent-matters-updated')
 * @param {(item: any) => any} cfg.snapshot  extract stored shape from a full item (default: {id, title, viewedAt})
 * @param {(entry: any) => boolean} cfg.isValid  validate an entry on read (default: id + title)
 */
export function makeRecentTracker(cfg) {
  const KEY = cfg.storageKey
  const EVT = cfg.eventName
  const snapshot = cfg.snapshot || (({ id, title }) => ({ id, title }))
  const isValid = cfg.isValid || ((e) => e && e.id && e.title)

  const dispatch = () => {
    try { window.dispatchEvent(new Event(EVT)) } catch {}
  }

  const get = () => {
    try {
      const raw = localStorage.getItem(KEY)
      if (!raw) return []
      const list = JSON.parse(raw)
      if (!Array.isArray(list)) return []
      const now = Date.now()
      return list.filter((e) => isValid(e) && (now - (e.viewedAt || 0)) < MAX_AGE_MS)
    } catch { return [] }
  }

  const push = (item) => {
    const snap = snapshot(item)
    if (!snap || !snap.id) return
    try {
      const cur = get().filter((e) => e.id !== snap.id)
      const next = [{ ...snap, viewedAt: Date.now() }, ...cur].slice(0, MAX)
      localStorage.setItem(KEY, JSON.stringify(next))
      dispatch()
    } catch { /* localStorage full or blocked — non-fatal */ }
  }

  const remove = (id) => {
    if (!id) return
    try {
      const cur = get().filter((e) => e.id !== id)
      localStorage.setItem(KEY, JSON.stringify(cur))
      dispatch()
    } catch {}
  }

  const clear = () => {
    try {
      localStorage.removeItem(KEY)
      dispatch()
    } catch {}
  }

  return { get, push, remove, clear, EVENT: EVT }
}

// ── Concrete trackers ────────────────────────────────────────────────
export const recentMattersTracker  = makeRecentTracker({
  storageKey: 'lextrack_recent_matters',
  eventName:  'recent-matters-updated'
})

export const recentClientsTracker  = makeRecentTracker({
  storageKey: 'lextrack_recent_clients',
  eventName:  'recent-clients-updated'
})

export const recentContactsTracker = makeRecentTracker({
  storageKey: 'lextrack_recent_contacts',
  eventName:  'recent-contacts-updated'
})
